/** @jsxImportSource ../../i18n */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Check, Mic, MicOff, PhoneOff, SkipForward } from 'lucide-react';
import { sendMessage } from '../../store/slices/chatSlice';
import {
  loadVoices, pickVoice, primeSpeech, recordUtterance, ServerSpeaker, Speaker, SPEECH_ISSUES, transcribe,
} from '../../services/voice';
import { MASCOTS, mascotById } from '../mascotCatalog';
import { cx } from '../../theme/cx';
import { Banner, Button } from '../ui';

const PHASES = {
  connecting: 'Connexion au micro…',
  listening: 'Je vous écoute…',
  transcribing: 'Je note votre question…',
  thinking: 'Je réfléchis…',
  speaking: 'Gladys vous répond',
  paused: 'Micro coupé',
  error: 'Appel interrompu',
};

const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

const clock = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

// Hands-free call with Gladys over the chat panel: listen, transcribe,
// stream the answer through the regular chat (saved in the conversation)
// and speak it, then listen again.
// serverSpeech: the backend can read the replies (OpenAI): its voice is
// preferred to the browser's, which stays the fallback.
const VoiceCall = ({ onClose, mascotVariant, onPhase, voiceWaitMs = 1500, serverSpeech = false }) => {
  const dispatch = useDispatch();
  const conversationId = useSelector((s) => s.chat.currentConversation?.id || 0);
  const streamingText = useSelector((s) => {
    const msgs = s.chat.currentConversation?.messages;
    const last = msgs?.[msgs.length - 1];
    return last?.role === 'assistant' && last.streaming ? last.content : null;
  });
  const [phase, setPhase] = useState('connecting');
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState('');
  const [reply, setReply] = useState('');
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState('');
  // Why she does not speak (no voices, blocked, failed): she still answers in writing.
  const [speechIssue, setSpeechIssue] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [engine, setEngine] = useState(serverSpeech ? 'server' : 'browser');

  const alive = useRef(true);
  const mic = useRef(null);
  const recording = useRef(null);
  const speaker = useRef(null);
  const fed = useRef(0);
  const conv = useRef(conversationId);
  const mutedRef = useRef(false);
  const voice = useRef(null);
  // Which voice reads the replies: the server's, or the browser's.
  const engineRef = useRef(serverSpeech ? 'server' : 'browser');
  // Each question/answer turn; an interruption or a hang-up ends the turn.
  const turn = useRef(0);
  const replyText = useRef('');
  const replyDone = useRef(false);
  const serverSpeechRef = useRef(serverSpeech);
  // Settles once the browser's voices are known (the engine depends on it).
  const voicesReady = useRef(Promise.resolve());
  serverSpeechRef.current = serverSpeech;
  const hangUpRef = useRef(null);
  const mascot = mascotById(mascotVariant) || MASCOTS[0];

  useEffect(() => { conv.current = conversationId || conv.current; }, [conversationId]);
  useEffect(() => { onPhase?.(phase); }, [phase, onPhase]);
  useEffect(() => () => onPhase?.('idle'), [onPhase]);

  const listen = useCallback(async () => {
    // Half duplex, one loop: never a second recording at the same time.
    if (!alive.current || !mic.current || recording.current) return;
    if (mutedRef.current) { setPhase('paused'); return; }
    setError('');
    setPhase('listening');
    setLevel(0);
    const rec = recordUtterance(mic.current, { onLevel: setLevel });
    recording.current = rec;
    const blob = await rec.promise;
    recording.current = null;
    if (!alive.current) return;
    if (!blob) {
      if (mutedRef.current) setPhase('paused');
      else listen();
      return;
    }
    setPhase('transcribing');
    let text;
    try {
      text = await transcribe(blob);
    } catch (err) {
      if (!alive.current) return;
      if (err?.response?.status === 422) { listen(); return; }
      setError(errorText(err, 'La transcription a échoué.'));
      setPhase('error');
      return;
    }
    if (!alive.current) return;
    setHeard(text);
    setReply('');
    setPhase('thinking');
    fed.current = 0;
    replyText.current = '';
    replyDone.current = false;
    await voicesReady.current;
    if (!alive.current) return;
    turn.current += 1;
    const myTurn = turn.current;
    speaker.current = makeSpeaker(myTurn, engineRef.current);
    const result = await dispatch(sendMessage({ conversationId: conv.current, message: text, voice: true }));
    if (!alive.current || turn.current !== myTurn) return;
    if (sendMessage.rejected.match(result)) {
      speaker.current?.cancel();
      speaker.current = null;
      setError(result.payload || 'Gladys n’a pas pu répondre.');
      setPhase('error');
      return;
    }
    const final = result.payload?.message?.content || '';
    conv.current = result.payload?.conversation?.id || conv.current;
    // The speaker may have been swapped for the server voice meanwhile.
    if (final.length > fed.current) speaker.current?.push(final.slice(fed.current));
    fed.current = final.length;
    replyText.current = final;
    replyDone.current = true;
    setReply(final);
    speaker.current?.finish();
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // A speaker for one turn, with the server's voice or the browser's. When
  // one fails and the other can speak, the turn switches once to the other
  // and replays what was received so far.
  function makeSpeaker(myTurn, kind, fallback = true) {
    const callbacks = {
      onStart: () => { if (alive.current && turn.current === myTurn) setPhase('speaking'); },
      onEnd: () => {
        if (turn.current !== myTurn) return;
        speaker.current = null;
        listen();
      },
      onError: (issue, code) => {
        if (!alive.current || turn.current !== myTurn) return;
        const browserVoices = (window.speechSynthesis?.getVoices?.() || []).length > 0;
        const other = kind === 'server' ? browserVoices && 'browser' : serverSpeechRef.current && 'server';
        if (fallback && other) {
          setVoiceEngine(other);
          const old = speaker.current;
          const next = makeSpeaker(myTurn, other, false);
          speaker.current = next;
          old?.cancel();
          if (replyText.current) next.push(replyText.current);
          if (replyDone.current) next.finish();
          return;
        }
        setSpeechIssue({ kind: issue, code });
      },
    };
    return kind === 'server' ? new ServerSpeaker(callbacks) : new Speaker({ ...callbacks, voice: voice.current });
  }

  function setVoiceEngine(kind) {
    engineRef.current = kind;
    setEngine(kind);
  }

  // The reply streams into the chat state: speak each new piece.
  useEffect(() => {
    if (streamingText === null || !speaker.current) return;
    if (streamingText.length > fed.current) speaker.current.push(streamingText.slice(fed.current));
    fed.current = streamingText.length;
    replyText.current = streamingText;
    setReply(streamingText);
  }, [streamingText]);

  const hangUp = useCallback(() => {
    alive.current = false;
    turn.current += 1;
    recording.current?.abort();
    speaker.current?.cancel();
    speaker.current = null;
    mic.current?.getTracks().forEach((t) => t.stop());
    mic.current = null;
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    alive.current = true;
    // This mount only: a microphone granted after an unmount (or after
    // React's development double mount) must not start a second loop.
    let active = true;
    const synth = window.speechSynthesis;
    const loadVoice = () => { voice.current = pickVoice(synth?.getVoices?.() || []); };
    synth?.addEventListener?.('voiceschanged', loadVoice);
    voicesReady.current = loadVoices(synth, voiceWaitMs).then((voices) => {
      if (!active) return;
      voice.current = pickVoice(voices);
      if (voices.length > 0) return;
      if (serverSpeechRef.current) setVoiceEngine('server');
      else setSpeechIssue({ kind: 'no-voices' });
    });
    navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
      .then((stream) => {
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        mic.current = stream;
        listen();
      })
      .catch(() => {
        if (!active) return;
        setError('Accès au micro refusé : autorisez-le dans le navigateur pour parler à Gladys.');
        setPhase('error');
      });
    const tick = setInterval(() => setSeconds((s) => s + 1), 1000);
    hangUpRef.current?.focus();
    return () => {
      active = false;
      clearInterval(tick);
      synth?.removeEventListener?.('voiceschanged', loadVoice);
      alive.current = false;
      recording.current?.abort();
      speaker.current?.cancel();
      mic.current?.getTracks().forEach((t) => t.stop());
    };
  }, [listen, voiceWaitMs]);

  // Every button press is a user gesture: keep speech unlocked.
  const gesture = (fn) => () => {
    primeSpeech();
    fn();
  };

  const enableSpeech = () => {
    primeSpeech();
    setSpeechIssue(null);
  };

  const toggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    if (next) recording.current?.abort();
    else if (!recording.current && !speaker.current && phase !== 'transcribing' && phase !== 'thinking') listen();
  };

  const interrupt = () => {
    turn.current += 1;
    speaker.current?.cancel();
    speaker.current = null;
    listen();
  };

  const ringScale = phase === 'listening' ? 1 + level * 0.35 : 1;

  return (
    <div role="dialog" aria-label="Appel vocal avec Gladys" className="absolute inset-0 z-20 flex flex-col bg-surface-1" data-testid="voice-call">
      <div className="flex items-center justify-between border-b border-line px-5 py-3 text-sm">
        <span className="font-medium text-fg">Appel avec Gladys</span>
        <span className="num text-fg-subtle" aria-label="Durée de l’appel">{clock(seconds)}</span>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-6 overflow-y-auto px-6 py-8 text-center">
        <div className="relative h-40 w-40">
          <span
            aria-hidden="true"
            className={cx('absolute inset-0 rounded-full border-2 transition-transform duration-100', phase === 'speaking' ? 'animate-pulse-soft border-gold-400' : phase === 'listening' ? 'border-gold-500/70' : 'border-line-strong')}
            style={{ transform: `scale(${ringScale})` }}
            data-testid="voice-ring"
          />
          <span className={cx('block h-full w-full overflow-hidden rounded-full border border-line-strong bg-surface-2', phase === 'speaking' && 'ambient-sunny', phase === 'paused' && 'ambient-rest')}>
            <img src={mascot.img} alt="" className="h-full w-full origin-[50%_6%] scale-[1.9] object-cover object-top" />
          </span>
        </div>
        <p className="font-display text-xl text-fg" aria-live="polite" data-testid="voice-phase">{PHASES[phase]}</p>
        {engine === 'server' && <p className="-mt-4 text-xs text-fg-subtle" data-testid="voice-engine">Voix de synthèse du serveur (OpenAI)</p>}
        {(heard || reply) && (
          <div className="w-full max-w-md space-y-3 text-left text-sm leading-relaxed">
            {heard && <p className="rounded-control border border-line bg-surface-2 px-3 py-2 text-fg-body"><span className="text-fg-subtle">Vous : </span>{heard}</p>}
            {reply && <p className="rounded-control border border-gold-500/25 bg-gold-950/30 px-3 py-2 text-fg-body" data-testid="voice-reply"><span className="text-gold-300">Gladys : </span>{reply.length > 420 ? `…${reply.slice(-420)}` : reply}</p>}
          </div>
        )}
        {speechIssue && (
          <Banner
            tone="warn"
            className="w-full max-w-md text-left"
            data-testid="speech-issue"
            action={speechIssue.kind === 'blocked' ? <Button size="sm" variant="secondary" onClick={enableSpeech}>Activer la voix</Button> : null}
          >
            {SPEECH_ISSUES[speechIssue.kind]}
            {speechIssue.kind === 'failed' && speechIssue.code ? ` (erreur « ${speechIssue.code} »)` : ''}
          </Banner>
        )}
        {error && (
          <Banner tone="loss" role="alert" className="w-full max-w-md text-left" action={mic.current ? <Button size="sm" variant="secondary" onClick={listen}>Réessayer</Button> : null}>
            {error}
          </Banner>
        )}
      </div>

      <div className="flex items-center justify-center gap-4 border-t border-line px-5 py-5">
        <Button variant="secondary" leadingIcon={muted ? Mic : MicOff} onClick={gesture(toggleMute)} aria-pressed={muted} disabled={!mic.current && phase !== 'paused'}>
          {muted ? 'Réactiver le micro' : 'Couper le micro'}
        </Button>
        <button
          ref={hangUpRef}
          type="button"
          onClick={hangUp}
          aria-label="Raccrocher"
          title="Raccrocher"
          className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-loss-600 text-fg shadow-pop transition-colors hover:bg-loss-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-loss-300"
        >
          <PhoneOff size={22} aria-hidden="true" />
        </button>
        {phase === 'speaking'
          ? <Button variant="secondary" leadingIcon={SkipForward} onClick={gesture(interrupt)}>Couper la parole</Button>
          : <Button variant="secondary" leadingIcon={Check} onClick={gesture(() => recording.current?.stop())} disabled={phase !== 'listening'}>J’ai fini</Button>}
      </div>
    </div>
  );
};

export default VoiceCall;
